import React, { useEffect, useState } from 'react';
import { useSearchParams, useNavigate, Link } from 'react-router-dom';
import { useAuth, API } from '../App';
import { toast } from 'sonner';
import { CircleDot, CheckCircle, XCircle, Loader2, LogIn } from 'lucide-react';
import { Button } from '../components/ui/button';

/**
 * PaymentCallback - Handles Stripe payment return
 * 
 * This is a semi-public route that handles payment verification.
 * If the user is authenticated, it verifies and credits them.
 * If not authenticated, it prompts them to log in to claim credits.
 */
const PaymentCallback = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { token, user, updateUser, isLoading: authLoading } = useAuth();
  
  const [status, setStatus] = useState('verifying'); // verifying, success, error, need_login
  const [message, setMessage] = useState('');
  const [creditsAdded, setCreditsAdded] = useState(0);
  
  const sessionId = searchParams.get('session_id');

  useEffect(() => {
    if (authLoading) return; // Wait for auth to load
    
    if (!sessionId) {
      setStatus('error');
      setMessage('No payment session found');
      return;
    }

    if (!token) {
      // User needs to log in first
      setStatus('need_login');
      setMessage('Please log in to verify your payment and receive your credits.');
      // Store the session_id for after login
      localStorage.setItem('pending_payment_session', sessionId);
      return;
    }

    // User is authenticated - verify payment
    verifyPayment();
  }, [sessionId, token, authLoading]);

  const verifyPayment = async (attempts = 0) => {
    const maxAttempts = 10;
    const pollInterval = 2000;

    if (attempts >= maxAttempts) {
      setStatus('error');
      setMessage('Payment verification timed out. Please contact support if you were charged.');
      return;
    }

    try {
      const res = await fetch(`${API}/payments/status/${sessionId}`, {
        headers: { Authorization: `Bearer ${token}` },
        credentials: 'include',
      });

      if (res.status === 401 || res.status === 403) {
        setStatus('need_login');
        setMessage('Your session has expired. Please log in again to verify your payment.');
        localStorage.setItem('pending_payment_session', sessionId);
        return;
      }

      if (res.status === 404) {
        setStatus('error');
        setMessage('Payment session not found. This may be from a different account.');
        return;
      }

      if (res.ok) {
        const data = await res.json();

        if (data.payment_status === 'paid') {
          setStatus('success');
          setCreditsAdded(data.credits_added);
          setMessage(`${data.credits_added} credits have been added to your account!`);
          
          // Refresh user data
          const userRes = await fetch(`${API}/auth/me`, {
            headers: { Authorization: `Bearer ${token}` },
            credentials: 'include',
          });
          if (userRes.ok) {
            const userData = await userRes.json();
            updateUser({ credits: userData.credits });
          }
          
          toast.success(`${data.credits_added} credits added!`);
          localStorage.removeItem('pending_payment_session');
          return;
        } else if (data.status === 'expired') {
          setStatus('error');
          setMessage('This payment session has expired.');
          return;
        }
      }

      // Still processing - poll again
      setTimeout(() => verifyPayment(attempts + 1), pollInterval);
    } catch (err) {
      console.error('Payment verification error:', err);
      setTimeout(() => verifyPayment(attempts + 1), pollInterval);
    }
  };

  const handleGoToLogin = () => {
    navigate('/login', { state: { from: '/payment/callback', returnTo: `/payment/callback?session_id=${sessionId}` } });
  };

  return (
    <div className="min-h-screen bg-background flex items-center justify-center p-6">
      <div className="glass-card p-8 max-w-md w-full text-center">
        <Link to="/" className="flex items-center justify-center gap-2 mb-8">
          <CircleDot className="w-8 h-8 text-primary" />
          <span className="font-heading text-2xl">MandalaMuse</span>
        </Link>

        {status === 'verifying' && (
          <>
            <Loader2 className="w-16 h-16 mx-auto mb-6 text-primary animate-spin" />
            <h2 className="font-heading text-2xl mb-2">Verifying Payment</h2>
            <p className="text-muted-foreground">Please wait while we confirm your payment...</p>
          </>
        )}

        {status === 'success' && (
          <>
            <CheckCircle className="w-16 h-16 mx-auto mb-6 text-green-500" />
            <h2 className="font-heading text-2xl mb-2">Payment Successful!</h2>
            <p className="text-muted-foreground mb-6">{message}</p>
            <div className="text-4xl font-heading text-primary mb-6">+{creditsAdded}</div>
            <Button onClick={() => navigate('/credits')} className="w-full rounded-full">
              View Credits
            </Button>
          </>
        )}

        {status === 'error' && (
          <>
            <XCircle className="w-16 h-16 mx-auto mb-6 text-red-500" />
            <h2 className="font-heading text-2xl mb-2">Verification Failed</h2>
            <p className="text-muted-foreground mb-6">{message}</p>
            <div className="space-y-3">
              <Button onClick={() => navigate('/credits')} variant="outline" className="w-full rounded-full">
                Try Again
              </Button>
              <Button onClick={() => navigate('/dashboard')} className="w-full rounded-full">
                Go to Dashboard
              </Button>
            </div>
          </>
        )}

        {status === 'need_login' && (
          <>
            <LogIn className="w-16 h-16 mx-auto mb-6 text-primary" />
            <h2 className="font-heading text-2xl mb-2">Login Required</h2>
            <p className="text-muted-foreground mb-6">{message}</p>
            <div className="space-y-3">
              <Button onClick={handleGoToLogin} className="w-full rounded-full">
                <LogIn className="w-4 h-4 mr-2" />
                Log In to Claim Credits
              </Button>
              <p className="text-sm text-muted-foreground">
                Your payment has been processed. Log in to receive your credits.
              </p>
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default PaymentCallback;
